import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Reorder } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { supabase } from "@/integrations/supabase/client";
import { getCheckoutQuestions, createCheckoutQuestion, updateCheckoutQuestion, deleteCheckoutQuestion, restoreCheckoutQuestion } from "@/lib/api-checkout";
import { fieldTypeNeedsOptions, normalizeQuestionOptions } from "@/lib/checkout-form";
import { CheckoutQuestionsRulesModal } from "@/components/checkout-questions/CheckoutQuestionsRulesModal";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  panelBody,
  panelEmpty,
  panelHint,
  panelPage,
  panelPill,
  panelSurface,
  panelTitle,
} from "@/components/producer/panel";
import {
  EMPTY_ARRAY,
  emptyDraft,
  buildDraftFromQuestion,
  type DraftState,
  type TierPreview,
} from "./types";
import { NativeFieldsList } from "./NativeFieldsList";
import { QuestionEditorModal } from "./QuestionEditorModal";
import { QuestionAnswersModal } from "./QuestionAnswersModal";
import { FormPreviewModal } from "./FormPreviewModal";
import { QuestionRow } from "./QuestionRow";

export function CheckoutQuestionsBuilder({ eventId }: { eventId: string }) {
  const { t } = useTranslation("producerCheckoutQuestions");
  const queryClient = useQueryClient();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [activeDraft, setActiveDraft] = useState<DraftState>(emptyDraft());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
  const [answersModalQuestion, setAnswersModalQuestion] = useState<any | null>(null);
  
  const { data: questions = EMPTY_ARRAY, isLoading } = useQuery({
    queryKey: ["checkout-questions", eventId],
    queryFn: () => getCheckoutQuestions(eventId),
  });

  const { data: ticketTiers = EMPTY_ARRAY } = useQuery({
    queryKey: ["checkout-question-tiers", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ticket_tiers")
        .select("id, name, tier_type, is_visible, sort_order")
        .eq("event_id", eventId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []) as TierPreview[];
    },
  });

  const [localQuestions, setLocalQuestions] = useState<typeof questions>([]);
  const [lastDeleted, setLastDeleted] = useState<{ question: any; index: number } | null>(null);

  useEffect(() => {
    setLocalQuestions(questions);
  }, [questions]);

  const orderedQuestions = useMemo(() => [...localQuestions].sort((left, right) => left.sort_order - right.sort_order), [localQuestions]);

  const updateMut = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, any> }) => updateCheckoutQuestion(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checkout-questions", eventId] });
      setIsEditorOpen(false);
      setEditingQuestion(null);
      toast({ title: t("questions.toasts.updated", "Pergunta atualizada!") });
    },
    onError: (error: any) => toast({ title: t("questions.toasts.errorTitle", "Erro"), description: error.message, variant: "destructive" }),
  });

  const reorderMut = useMutation({
    mutationFn: (updated: any[]) => Promise.all(updated.map((q) => updateCheckoutQuestion(q.id, { sort_order: q.sort_order }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checkout-questions", eventId] });
    },
    onError: (e: any) => toast({ title: t("questions.toasts.errorTitle", "Erro"), description: e.message, variant: "destructive" }),
  });

  const createMut = useMutation({
    mutationFn: (payload: any) =>
      createCheckoutQuestion({
        ...payload,
        event_id: eventId,
        sort_order: localQuestions.length,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checkout-questions", eventId] });
      setIsEditorOpen(false);
      toast({ title: t("questions.toasts.created", "Pergunta adicionada!") });
    },
    onError: (e: any) => toast({ title: t("questions.toasts.errorTitle", "Erro"), description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteCheckoutQuestion,
    onSuccess: (_data, id) => {
      const question = localQuestions.find((q) => q.id === id);
      const index = localQuestions.findIndex((q) => q.id === id);
      if (question && index !== -1) setLastDeleted({ question, index });
      queryClient.invalidateQueries({ queryKey: ["checkout-questions", eventId] });
      toast({
        title: t("questions.toasts.removed", "Pergunta removida"),
        action: <ToastAction altText={t("questions.toasts.undoAlt", "Desfazer")} onClick={undoDelete}>{t("questions.toasts.undo", "Desfazer")}</ToastAction>,
      });
    },
    onError: (e: any) => toast({ title: t("questions.toasts.errorTitle", "Erro"), description: e.message, variant: "destructive" }),
  });

  const handleReorder = (nextQuestions: any[]) => {
    const updated = nextQuestions.map((item, idx) => ({ ...item, sort_order: idx }));
    setLocalQuestions(updated);
    reorderMut.mutate(updated);
  };

  const moveQuestion = (index: number, delta: number) => {
    const next = index + delta;
    if (next < 0 || next >= localQuestions.length) return;
    const updated = Array.from(localQuestions);
    const [moved] = updated.splice(index, 1);
    updated.splice(next, 0, moved);
    handleReorder(updated);
  };

  const undoDelete = async () => {
    if (!lastDeleted) return;
    const { question, index } = lastDeleted;
    try {
      await restoreCheckoutQuestion(question.id, index);
      queryClient.invalidateQueries({ queryKey: ["checkout-questions", eventId] });
      setLastDeleted(null);
    } catch (error: any) {
      toast({ title: t("questions.toasts.errorTitle", "Erro"), description: error.message, variant: "destructive" });
    }
  };

  const isDuplicateQuestion = (text: string, excludeId?: string) => {
    const normalized = text.trim().toLowerCase();
    return localQuestions.some((q) => q.id !== excludeId && q.question?.trim().toLowerCase() === normalized);
  };

  const handleEditorSave = () => {
    const trimmed = activeDraft.question.trim();
    if (!trimmed) {
      setValidationError("A pergunta não pode estar vazia.");
      return;
    }

    if (isDuplicateQuestion(trimmed, editingQuestion?.id)) {
      setValidationError("Já existe uma pergunta com esse nome.");
      return;
    }

    const isTextValidation = activeDraft.field_type === "text" && activeDraft.is_validation_enabled;
    let options: any = fieldTypeNeedsOptions(activeDraft.field_type as any)
      ? normalizeQuestionOptions(activeDraft.optionsText)
      : null;

    if (isTextValidation) {
      options = {
        isValidationEnabled: true,
        codes: (activeDraft.validation_codes || "").split(/\r?\n/).map(c => c.trim()).filter(Boolean)
      };
      if (options.codes.length === 0) {
        setValidationError("A lista de códigos permitidos não pode estar vazia.");
        return;
      }
    }

    if (fieldTypeNeedsOptions(activeDraft.field_type as any) && (!options || options.length < 1)) {
      setValidationError("Adicione ao menos uma opção (separe teclando Enter).");
      return;
    }

    setValidationError(null);

    const payload = {
      question: trimmed,
      field_type: activeDraft.field_type,
      options,
      is_required: isTextValidation ? true : activeDraft.is_required,
      applies_to: activeDraft.applies_to,
      tier_ids: activeDraft.tier_ids,
    };

    if (editorMode === "create") {
      createMut.mutate(payload);
    } else {
      updateMut.mutate({ id: editingQuestion.id, updates: payload });
    }
  };

  const openCreateModal = () => {
    setEditorMode("create");
    setActiveDraft(emptyDraft());
    setEditingQuestion(null);
    setValidationError(null);
    setIsEditorOpen(true);
  };

  const openEditModal = (question: any) => {
    setEditorMode("edit");
    setActiveDraft(buildDraftFromQuestion(question));
    setEditingQuestion(question);
    setValidationError(null);
    setIsEditorOpen(true);
  };

  const lastUpdated = useMemo(() => {
    if (!localQuestions || localQuestions.length === 0) return null;
    const timestamps = localQuestions
      .map((q: any) => q.updated_at || q.created_at)
      .filter(Boolean)
      .map((ts: string) => new Date(ts).getTime());
    if (timestamps.length === 0) return null;
    const maxTs = Math.max(...timestamps);
    return new Date(maxTs).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [localQuestions]);

  return (
    <div className={panelPage}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={panelTitle}>Formulário</h1>
          {lastUpdated && (
            <p className={panelHint}>Atualizado em {lastUpdated}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            onClick={() => setIsRulesOpen(true)}
            variant="ghost"
            size="sm"
            className={cn("h-9 text-muted-foreground hover:text-foreground", panelPill)}
          >
            Como funciona
          </Button>
          <Button
            onClick={() => setIsPreviewOpen(true)}
            variant="outline"
            size="sm"
            className={cn("h-9", panelPill)}
          >
            Preview
          </Button>
          <Button onClick={openCreateModal} size="sm" className={cn("h-9 px-4", panelPill)}>
            Adicionar
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className={cn(panelSurface, "flex items-center justify-center py-20")}>
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className={panelSurface}>
            <div className={panelBody}>
              <NativeFieldsList />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 px-1">
              <p className="text-sm font-medium text-foreground">Perguntas extras</p>
              {reorderMut.isPending && (
                <p className={panelHint}>Salvando ordem…</p>
              )}
            </div>

            {orderedQuestions.length > 0 ? (
              <Reorder.Group axis="y" values={orderedQuestions} onReorder={handleReorder} className="space-y-2 pb-safe">
                {orderedQuestions.map((question: any, index: number) => (
                  <QuestionRow
                    key={question.id}
                    question={question}
                    index={index}
                    total={orderedQuestions.length}
                    tiers={ticketTiers}
                    onMove={moveQuestion}
                    onEdit={openEditModal}
                    onDelete={(q) => deleteMut.mutate(q.id)}
                    onOpenAnswers={(q) => setAnswersModalQuestion(q)}
                    reorderDisabled={reorderMut.isPending || updateMut.isPending}
                  />
                ))}
              </Reorder.Group>
            ) : (
              <div className={cn(panelEmpty, "flex flex-col items-center justify-center px-6 py-12 text-center")}>
                <p className="text-sm font-medium text-foreground">Nenhuma pergunta extra</p>
                <p className={cn(panelHint, "max-w-sm")}>
                  Os 4 campos nativos já cobrem identificação. Adicione só o que for específico do evento.
                </p>
                <Button onClick={openCreateModal} size="sm" className={cn("mt-4", panelPill)}>
                  Adicionar pergunta
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <QuestionEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        mode={editorMode}
        draft={activeDraft}
        setDraft={setActiveDraft}
        tiers={ticketTiers}
        onSave={handleEditorSave}
        isSaving={createMut.isPending || updateMut.isPending}
        validationError={validationError}
      />

      <QuestionAnswersModal
        isOpen={!!answersModalQuestion}
        onClose={() => setAnswersModalQuestion(null)}
        question={answersModalQuestion}
      />

      <CheckoutQuestionsRulesModal open={isRulesOpen} onOpenChange={setIsRulesOpen} />

      <FormPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        questions={orderedQuestions}
        tiers={ticketTiers}
      />
    </div>
  );
}
